import { useMemo } from 'react';

import { useGetFirmQuery } from '@/services/api/firmsService';

import {
  getEffectiveAnswer,
  getEscalationReasons,
  PARENT_QUESTIONS,
} from '../constants/qualificationRules';
import { QualificationResult, ScreeningAnswers } from '../types';

interface QualificationCriteria {
  date_of_loss_max_days?: number | null;
  accepted_states?: string[];
  case_type_actions?: Record<string, 'accept' | 'reject' | 'escalate'>;
  default_case_type_action?: 'accept' | 'reject' | 'escalate';
}

interface IntakeData {
  caseType: string;
  state: string;
  dateOfLoss: string;
}

/**
 * Calculate days since a given date.
 */
const calculateDaysSince = (dateStr: string): number => {
  if (!dateStr) return 0;
  const date = new Date(dateStr);
  const today = new Date();
  const diffTime = today.getTime() - date.getTime();
  return Math.floor(diffTime / (1000 * 60 * 60 * 24));
};

/**
 * Calculate qualification status based on answers and firm criteria.
 *
 * Based on Legal Ops criteria (Dec 2024):
 * - QUALIFIED: ALL 5 parent categories are "Yes" (or Unk with override) AND firm criteria met
 * - ESCALATE: ANY parent is "No" OR (Unk without override) OR firm criteria not met
 * - PENDING: Questions not yet answered
 *
 * Note: Per new spec, answer-based issues lead to ESCALATE, not DISQUALIFY.
 * Only firm preference violations (rejected case type) cause immediate disqualification.
 */
export const calculateQualification = (
  answers: ScreeningAnswers,
  firmCriteria: QualificationCriteria | null,
  intakeData: IntakeData,
): QualificationResult => {
  const escalationReasons: string[] = [];

  // =========================================
  // FIRM CRITERIA CHECKS
  // =========================================

  // Case type rejected by firm → Disqualify (hard stop)
  if (firmCriteria && intakeData.caseType) {
    const action =
      firmCriteria.case_type_actions?.[intakeData.caseType] ??
      firmCriteria.default_case_type_action;
    if (action === 'reject') {
      return {
        status: 'disqualified',
        reasons: [`Firm doesn't accept ${intakeData.caseType} cases`],
      };
    }
    // Case type requires escalation
    if (action === 'escalate') {
      escalationReasons.push(
        `Case type ${intakeData.caseType} requires review`,
      );
    }
  }

  // Date of loss exceeds limit → Escalate
  if (firmCriteria?.date_of_loss_max_days && intakeData.dateOfLoss) {
    const daysSince = calculateDaysSince(intakeData.dateOfLoss);
    if (daysSince > firmCriteria.date_of_loss_max_days) {
      escalationReasons.push(
        `Date of loss exceeds ${firmCriteria.date_of_loss_max_days} day limit`,
      );
    }
  }

  // State not accepted → Escalate
  if (
    firmCriteria?.accepted_states?.length &&
    intakeData.state &&
    !firmCriteria.accepted_states.includes(intakeData.state)
  ) {
    escalationReasons.push(`State ${intakeData.state} not accepted by firm`);
  }

  // =========================================
  // ANSWER-BASED CHECKS
  // =========================================
  // Note: Coverage issues (defendant not insured) now lead to escalation,
  // not disqualification. This is handled in getEscalationReasons.

  // Get escalation reasons from answer state
  const answerEscalationReasons = getEscalationReasons(answers);
  escalationReasons.push(...answerEscalationReasons);

  // If any escalation reasons, escalate
  if (escalationReasons.length > 0) {
    return { status: 'escalate', reasons: escalationReasons };
  }

  // =========================================
  // CHECK IF ALL REQUIREMENTS MET (QUALIFIED)
  // =========================================

  // Check if all parent questions have effective "yes" answers
  const allParentsYes = PARENT_QUESTIONS.every(({ key }) => {
    const effectiveAnswer = getEffectiveAnswer(answers, key);
    return effectiveAnswer === 'yes';
  });

  if (allParentsYes) {
    return { status: 'qualified', reasons: ['All requirements met'] };
  }

  // =========================================
  // PENDING (Incomplete answers)
  // =========================================

  // Find which questions are still pending
  const pendingQuestions = PARENT_QUESTIONS.filter(({ key }) => {
    const answer = answers[key];
    return !answer || answer === 'unknown';
  }).map(({ label }) => label);

  return {
    status: 'pending',
    reasons:
      pendingQuestions.length > 0
        ? [`Awaiting: ${pendingQuestions.join(', ')}`]
        : ['Incomplete answers'],
  };
};

/**
 * Hook to calculate qualification status in real-time.
 * Accepts primitive values for intake data to ensure proper dependency tracking.
 */
export const useQualificationEngine = (
  firmId: string | undefined,
  answers: ScreeningAnswers,
  caseType: string,
  state: string,
  dateOfLoss: string,
): QualificationResult => {
  const { data: firm } = useGetFirmQuery(firmId ?? '', {
    skip: !firmId,
  });

  const qualificationCriteria = useMemo((): QualificationCriteria | null => {
    if (!firm?.intake_preferences?.qualification_criteria) {
      return null;
    }
    return firm.intake_preferences.qualification_criteria;
  }, [firm]);

  // Use primitive dependencies to ensure recalculation when values change
  const result = useMemo(() => {
    const intakeData: IntakeData = { caseType, state, dateOfLoss };
    return calculateQualification(answers, qualificationCriteria, intakeData);
  }, [answers, qualificationCriteria, caseType, state, dateOfLoss]);

  return result;
};
